/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";
import {
  SequenceRef,
  SequenceRef$inboundSchema,
  SequenceRef$Outbound,
  SequenceRef$outboundSchema,
} from "./sequenceref.js";

/**
 * The type/version of the sequencer.
 */
export const SequencerEditorConfigType = {
  CondenserSequencerV1: "CondenserSequencerV1",
  CondenserSequencerV2: "CondenserSequencerV2",
  CompressorSequencerV1: "CompressorSequencerV1",
} as const;
/**
 * The type/version of the sequencer.
 */
export type SequencerEditorConfigType = OpenEnum<
  typeof SequencerEditorConfigType
>;

/**
 * The sequencer editor configuration, this gives information that the UI needs to build/display the sequencer editor.
 */
export type SequencerEditorConfig = {
  /**
   * A reference to a reading or setting.
   */
  activeSequenceRef?: Ref | undefined;
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  cosmicConditionExpression?: ConditionExpression | undefined;
  /**
   * A reference to a reading or setting.
   */
  cosmicFallbackStageRef?: Ref | undefined;
  /**
   * A reference to a reading or setting.
   */
  currentStageRef?: Ref | undefined;
  /**
   * If the sequencer editor is misconfigured, this will be populated with the reason why.
   */
  misconfiguredMessage?: Array<string> | undefined;
  /**
   * The sequences to display in the UI. The actual sequence data is stored in these refs.
   */
  sequences?: Array<SequenceRef> | undefined;
  /**
   * Setting refs for other compressor sequencer tables at this facility. Used by the UI to detect swing compressors assigned in sibling sequencers.
   */
  siblingSequences?: Array<SequenceRef> | undefined;
  /**
   * The type/version of the sequencer.
   */
  type: SequencerEditorConfigType;
};

/** @internal */
export const SequencerEditorConfigType$inboundSchema: z.ZodType<
  SequencerEditorConfigType,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(SequencerEditorConfigType);
/** @internal */
export const SequencerEditorConfigType$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  SequencerEditorConfigType
> = openEnums.outboundSchema(SequencerEditorConfigType);

/** @internal */
export const SequencerEditorConfig$inboundSchema: z.ZodType<
  SequencerEditorConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  active_sequence_ref: types.optional(Ref$inboundSchema),
  cosmic_condition_expression: types.optional(
    ConditionExpression$inboundSchema,
  ),
  cosmic_fallback_stage_ref: types.optional(Ref$inboundSchema),
  current_stage_ref: types.optional(Ref$inboundSchema),
  misconfigured_message: types.optional(z.array(types.string())),
  sequences: types.optional(z.array(SequenceRef$inboundSchema)),
  sibling_sequences: types.optional(z.array(SequenceRef$inboundSchema)),
  type: SequencerEditorConfigType$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "active_sequence_ref": "activeSequenceRef",
    "cosmic_condition_expression": "cosmicConditionExpression",
    "cosmic_fallback_stage_ref": "cosmicFallbackStageRef",
    "current_stage_ref": "currentStageRef",
    "misconfigured_message": "misconfiguredMessage",
    "sibling_sequences": "siblingSequences",
  });
});
/** @internal */
export type SequencerEditorConfig$Outbound = {
  active_sequence_ref?: Ref$Outbound | undefined;
  cosmic_condition_expression?: ConditionExpression$Outbound | undefined;
  cosmic_fallback_stage_ref?: Ref$Outbound | undefined;
  current_stage_ref?: Ref$Outbound | undefined;
  misconfigured_message?: Array<string> | undefined;
  sequences?: Array<SequenceRef$Outbound> | undefined;
  sibling_sequences?: Array<SequenceRef$Outbound> | undefined;
  type: string;
};

/** @internal */
export const SequencerEditorConfig$outboundSchema: z.ZodType<
  SequencerEditorConfig$Outbound,
  z.ZodTypeDef,
  SequencerEditorConfig
> = z.object({
  activeSequenceRef: Ref$outboundSchema.optional(),
  cosmicConditionExpression: ConditionExpression$outboundSchema.optional(),
  cosmicFallbackStageRef: Ref$outboundSchema.optional(),
  currentStageRef: Ref$outboundSchema.optional(),
  misconfiguredMessage: z.array(z.string()).optional(),
  sequences: z.array(SequenceRef$outboundSchema).optional(),
  siblingSequences: z.array(SequenceRef$outboundSchema).optional(),
  type: SequencerEditorConfigType$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    activeSequenceRef: "active_sequence_ref",
    cosmicConditionExpression: "cosmic_condition_expression",
    cosmicFallbackStageRef: "cosmic_fallback_stage_ref",
    currentStageRef: "current_stage_ref",
    misconfiguredMessage: "misconfigured_message",
    siblingSequences: "sibling_sequences",
  });
});

export function sequencerEditorConfigToJSON(
  sequencerEditorConfig: SequencerEditorConfig,
): string {
  return JSON.stringify(
    SequencerEditorConfig$outboundSchema.parse(sequencerEditorConfig),
  );
}
export function sequencerEditorConfigFromJSON(
  jsonString: string,
): SafeParseResult<SequencerEditorConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SequencerEditorConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SequencerEditorConfig' from JSON`,
  );
}
